import React, { useState } from "react";
import {
  Box,
  Link,
  TextField,
  IconButton,
  Typography,
  InputAdornment,
  Container,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { useNavigate, useLocation } from "react-router-dom"; // Added useLocation
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import BASE_URL from "../../../config/apiConfig";
import Cookies from "js-cookie";

// Email validation
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function PricingSignIn() {
  const navigate = useNavigate();
  const location = useLocation(); // To retrieve the passed state
  const { selectedPlan, redirectTo } = location.state || {}; // Destructure passed state

  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [passwordError, setPasswordError] = useState(false);

  const saveToken = (token) => {
    // Set a cookie that expires in 7 days
    try {
      Cookies.set("user_auth_token", token, { expires: 7 });
      Cookies.set("user_email", email, { expires: 7 });
      console.log(
        "User auth token and email saved successfully:",
        token,
        email
      );
    } catch (error) {
      console.error("Error saving user token and email:", error);
    }
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    setLoading(true);

    setEmailError(false);
    setPasswordError(false);

    if (!emailRegex.test(email)) {
      setEmailError(true);
      setLoading(false);
      return;
    }

    if (password.length < 6) {
      setPasswordError(true);
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${BASE_URL}/api/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password }),
      });

      if (response.ok) {
        const data = await response.json();
        console.log("login success:", data);
        console.log("token:", data.token);

        saveToken(data.token);

        // Redirect user to either the payment page with selectedPlan or another page
        if (selectedPlan) {
          navigate("/payment", { state: { selectedPlan } });
        } else if (redirectTo) {
          navigate(redirectTo, { state: { selectedPlan } });
        } else {
          // If no plan is passed, navigate to a default page
          navigate("/dashboard");
        }
      } else {
        console.error("Login error");
      }
    } catch (error) {
      console.error("Error during login:", error);
    } finally {
      setLoading(false);
    }
  };

  const renderForm = (
    <Box
      component="form"
      onSubmit={handleSignIn}
      display="flex"
      flexDirection="column"
      alignItems="stretch"
      sx={{ width: "100%", maxWidth: "400px", mb: 2 }}
    >
      <TextField
        fullWidth
        name="email"
        label="Email address"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        error={emailError}
        helperText={emailError ? "Please enter a valid email address" : ""}
        sx={{
          mb: 2,
          "& label.Mui-focused": { color: "#51833a" },
          "& .MuiOutlinedInput-root": {
            "&:hover fieldset": { borderColor: "#51833a" },
            "&.Mui-focused fieldset": { borderColor: "#51833a" },
          },
        }}
      />

      <TextField
        fullWidth
        name="password"
        label="Password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        type={showPassword ? "text" : "password"}
        error={passwordError}
        helperText={
          passwordError ? "Password must be at least 6 characters" : ""
        }
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => setShowPassword(!showPassword)}
                edge="end"
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{
          mb: 3,
          "& label.Mui-focused": { color: "#51833a" },
          "& .MuiOutlinedInput-root": {
            "&:hover fieldset": { borderColor: "#51833a" },
            "&.Mui-focused fieldset": { borderColor: "#51833a" },
          },
        }}
      />

      <Box sx={{ textAlign: "right", mb: 2 }}>
        <Link
          href="/user/recoverpassword"
          variant="body2"
          color="inherit"
          sx={{
            fontSize: "0.9rem",
            textDecoration: "none",
            "&:hover": { textDecoration: "underline", color: "#51833a" },
          }}
        >
          Forgot password?
        </Link>
      </Box>

      <LoadingButton
        fullWidth
        size="large"
        type="submit"
        color="inherit"
        variant="contained"
        loading={loading}
        sx={{
          background: "linear-gradient(45deg, #51833a 30%, #66bb6a 90%)",
          color: "#fff",
          "&:hover": { backgroundColor: "#406027" },
          mb: 2,
          textTransform: "none",
          minHeight: "50px",
          borderRadius: "8px",
          transition: "0.3s ease",
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)", // Subtle shadow effect
        }}
      >
        Sign in
      </LoadingButton>
    </Box>
  );

  return (
    <Container
      sx={{
        height: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: { xs: 2, md: 0 },
        background: "linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)", // Gradient background
      }}
    >
      <Box
        sx={{
          backgroundColor: "#fff",
          minHeight: "70vh",
          borderRadius: "16px",
          boxShadow: "0px 10px 20px rgba(0, 0, 0, 0.1)",
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          width: "100%",
          maxWidth: "900px",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            padding: { xs: 3, md: 5 },
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "flex-start",
            backgroundColor: "#f9fafb",
          }}
        >
          <Box
            gap={1.5}
            display="flex"
            flexDirection="column"
            alignItems="flex-start"
            sx={{ mb: 5 }}
          >
            <Typography
              variant="h5"
              sx={{
                fontWeight: "bold",
                color: "#333",
                letterSpacing: "1px",
                textTransform: "uppercase",
              }}
            >
              Sign in
            </Typography>
            <Typography variant="body2" sx={{ color: "#666" }}>
              Don’t have an account?{" "}
              <Link
                href="/pricing-signup"
                variant="body2"
                sx={{
                  textDecoration: "none",
                  color: "#51833a",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                Create account
              </Link>
            </Typography>
          </Box>
          {renderForm}
        </Box>
      </Box>
    </Container>
  );
}

export default PricingSignIn;
